import type {
    AnalyzeDocumentLanguagesRequest,
    AnalyzeDocumentLanguagesResponse,
    ApplyDocumentTrimRequest,
    ApplyDocumentTrimResponse,
    RestoreDocumentTrimRequest,
    RestoreDocumentTrimResponse,
} from '@rock/types';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Run per-page language analysis on a candidate's staged bytes (by document/candidate content hash).
 * The result also lands on the candidate doc (`languageAnalysis`), so the UI updates via subscription.
 */
export async function analyzeDocumentLanguages(
    request: AnalyzeDocumentLanguagesRequest,
): Promise<AnalyzeDocumentLanguagesResponse> {
    const fn = httpsCallable<AnalyzeDocumentLanguagesRequest, AnalyzeDocumentLanguagesResponse>(
        getFunctions(),
        'analyzeDocumentLanguages',
    );
    const result = await fn(request);
    return result.data;
}

/**
 * Cut pages out of a document's or candidate's PDF, IN PLACE — the record keeps its id, links, tags and
 * coverage, and only its bytes change. The server re-validates the ranges against the real PDF (and
 * re-expresses them against the original when cutting an already-cut record); the human's call is
 * recorded for proposal-accuracy stats.
 */
export async function applyDocumentTrim(request: ApplyDocumentTrimRequest): Promise<ApplyDocumentTrimResponse> {
    const fn = httpsCallable<ApplyDocumentTrimRequest, ApplyDocumentTrimResponse>(getFunctions(), 'applyDocumentTrim');
    const result = await fn(request);
    return result.data;
}

/** Serve the full original again, discarding every cut. It was never overwritten, so this moves no bytes. */
export async function restoreDocumentTrim(request: RestoreDocumentTrimRequest): Promise<RestoreDocumentTrimResponse> {
    const fn = httpsCallable<RestoreDocumentTrimRequest, RestoreDocumentTrimResponse>(
        getFunctions(),
        'restoreDocumentTrim',
    );
    const result = await fn(request);
    return result.data;
}
